import {useParams} from "react-router";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useEffect, useState} from "react";
import {handleError, initialForm, validate} from "./absence-form-utils.js";
import {useMutation, useQuery} from "@tanstack/react-query";
import AbsenceService from "../../../../../services/web/absence-service.js";
import absenceService from "../../../../../services/web/absence-service.js";
import AbsenceReasons from "../../utils/i18n.js";
import projectUtils from "../../../../../utils/project-utils.js";
import MySwal from "sweetalert2";
import {isBefore} from "date-fns";
import AbsenceTypeService from "../../../../../services/web/absence-type-service.js";

export const useAbsenceForm = () => {
  const { id } = useParams();
  const navigate = navigateHook();
  const [form, setForm] = useState(initialForm);
  const [userInput, setUserInput] = useState("");

  const {  data: usersSummary = { warNames: [], names: [] }, isLoading: isLoadingUsersSummary, refetch: refetchUsersSummary } = useQuery({
    queryKey: ['usersSummary'],
    queryFn: async () => {
      const { data } = await AbsenceService.usersSummary();

      const warNames = data.map(el => ({
        value: el.id,
        label: el.namewar
      }));

      const names = data.map(el => ({
        value: el.id,
        label: el.name
      }));

      return {
        warNames,
        names
      };
    },
  });

  const { data: absenceTypes = [], isLoading: isLoadingAbsenceTypes } = useQuery({
    queryKey: ["absence-types"],
    queryFn: async () => (await AbsenceTypeService.list()).map(({id, name}) => ({value: id, label: name}))
  });

  const { data: fetchedAbsence = null, isLoading: isLoadingAbsence, refetch: refetchAbsence } = useQuery({
    queryFn: () => absenceService.getById(id),
    queryKey: ['fetchedAbsence'],
    enabled: !!id
  })

  const { data: fetchedImage = null, isLoading: isLoadingImage } = useQuery({
    queryFn: () => absenceService.image(fetchedAbsence?.attachId),
    queryKey: ['fetchedImage', fetchedAbsence?.attachId],
    enabled: !!fetchedAbsence?.attachId
  })

  const refetchAll = () => {
    refetchUsersSummary();
    refetchAbsence();
  }

  const saveMutation = useMutation({
    mutationFn: (payload) => absenceService.save(payload),
    onSuccess: (data) => {
      projectUtils.success(!id);
      if(id) {
        refetchAll();
      } else {
        navigate( `/absences/form/${data}`, false)
      }
    },
    onError: (error) => {
      handleError(error);
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => absenceService.delete(id),
    onSuccess: () => {
      projectUtils.success(true, "Excluído com sucesso.");
      navigate(`/absences`);
    },
    onError: (error) => {
      handleError(error);
    }
  })

  useEffect( () => {
    if(!id || !fetchedAbsence) return;

    const reason = AbsenceReasons.getAbsenceReasons().find(option =>
      option.value === fetchedAbsence.name || option.label === fetchedAbsence.typeName
    )?.value ?? "";

    setForm(prevState => ({...prevState,
      id: fetchedAbsence.id,
      user_id: fetchedAbsence.userId,
      type_id: fetchedAbsence.typeId,
      observation: fetchedAbsence.observation,
      start_date: fetchedAbsence.startDate ? new Date(`${fetchedAbsence.startDate.split('T')[0]}T00:00:00`) : null,
      end_date: fetchedAbsence.endDate ? new Date(`${fetchedAbsence.endDate.split('T')[0]}T00:00:00`) : null,
      attach_id: fetchedAbsence.attachId ?? "",}))
  },[fetchedAbsence, id])

  useEffect(() => {
    if(!id || !fetchedAbsence || !fetchedImage) return;

    const previewUrl = URL.createObjectURL(fetchedImage);
    const image = {
      type: fetchedImage.type,
      extension: fetchedImage.type,
      url: previewUrl,
      file: fetchedImage
    };

    setForm(prevState => ({...prevState, attach: image}));

    return () => {
      URL.revokeObjectURL(previewUrl);
    };
  }, [fetchedImage]);

  const handleSelectChange = (selected, field) => {
    if(selected === null) {
      setUserInput("");
    } else {
      if(field === "name"){
        setUserInput("name");
      } else {
        setUserInput("namewar");
      }
    }

    setForm(prevState => ({...prevState, user_id: selected?.value ?? ""}));
  }
  const handleSave = () => {
    const isNotValid = validate(form);

    if(isNotValid){
      projectUtils.swalErrorMessage("", `O campo "${isNotValid}" é obrigatório.`, "Ok");
      return;
    }

    if(isBefore(form.end_date, form.start_date)) {
      MySwal.fire({title: "",
        text:`A data inicial não pode ser maior que a final.`,
        icon: "error",
        type: "error", confirmButtonText: "Ok",
        confirmButtonColor: '#063A6C'})
      return;
    }

    const formData = new FormData();
    const effectiveDayOff = {
      id: form.id,
      typeId: form.type_id,
      startDate: form.start_date.toISOString().split("T")[0],
      endDate: form.end_date.toISOString().split("T")[0],
      userId: form.user_id,
      observation: form.observation,
      attachId: form.attach ? (form.attach_id ?? "") : ""
    };

    formData.append("effectiveDayOffDto", new Blob([JSON.stringify(effectiveDayOff)], { type: "application/json" }));

    if(form.attach?.file){
      formData.append("file", form.attach.file);
    }

    saveMutation.mutate(formData);
  }

  const destroy = () => {
    MySwal.fire({
      title: "Excluir",
      text: 'Deseja excluir a ausência?',
      icon: "warning",
      confirmButtonText: "Sim",
      confirmButtonColor: "#063A6C",
      cancelButtonText: "Não",
      cancelButtonColor: "#c10007",
      showCancelButton: true,
      reverseButtons: true,
    }).then(({isConfirmed}) => {
      if (isConfirmed) {
        deleteMutation.mutate(id);
      }
    });
  }

  return {
    id,
    form,
    setForm,
    userInput,
    setUserInput,
    usersSummary,
    absenceTypes,
    isLoading: isLoadingAbsence || isLoadingAbsenceTypes || isLoadingUsersSummary || isLoadingImage || saveMutation.isPending || deleteMutation.isPending,
    navigate,
    handleSelectChange,
    handleSave,
    destroy
  };
}